import React from 'react';
import { ProgressBar } from '@fluentui/react-components';
import './GuideComponents.css';

/**
 * Barra de progreso de la guía (spec 008). La guía no dibuja una: se deriva de la tarjeta KPI (rótulo + número
 * tabular) y del primario. Riel en `borde`, relleno en primario (alto contraste: amarillo sobre riel con borde
 * blanco), etapa a la izquierda (14, bold en `medium`) y porcentaje tabular a la derecha. Sin `value` es
 * indeterminada (en cola, el backend todavía no informó avance). `role="progressbar"` con `aria-valuenow` y
 * `aria-valuetext` (etapa + porcentaje): el estado nunca depende sólo del color.
 */

export type GuideProgressTone = 'brand' | 'success' | 'error';

export interface GuideProgressProps {
  /** 0..100; undefined = indeterminada. */
  value?: number;
  /** Etapa o rótulo ("Leyendo con IA"). */
  label?: React.ReactNode;
  /** Texto secundario debajo de la barra (13). */
  detail?: React.ReactNode;
  /** `medium` (bloque del asistente) o `small` (banner y tablas). */
  size?: 'medium' | 'small';
  tone?: GuideProgressTone;
  /** Nombre accesible de la barra; por defecto el `label` si es texto. */
  ariaLabel?: string;
  /** Texto accesible del valor (por defecto "{value} %"). */
  valueText?: string;
  className?: string;
  'data-testid'?: string;
}

export const GuideProgress = ({ value, label, detail, size = 'medium', tone = 'brand', ariaLabel, valueText, className, ...rest }: GuideProgressProps) => {
  const clamped = value === undefined || Number.isNaN(value) ? undefined : Math.max(0, Math.min(100, Math.round(value)));
  const name = ariaLabel ?? (typeof label === 'string' ? label : undefined);
  const percentText = clamped === undefined ? undefined : `${clamped} %`;
  return (
    <div className={['ichtys-progress', `ichtys-progress--${size}`, className].filter(Boolean).join(' ')} data-tone={tone} data-testid={rest['data-testid']}>
      {(label || percentText) && (
        <div className="ichtys-progress__head">
          {label && <span className="ichtys-progress__label">{label}</span>}
          {percentText && (
            <span className="ichtys-progress__value ichtys-num" aria-hidden>
              {percentText}
            </span>
          )}
        </div>
      )}
      <ProgressBar
        className="ichtys-progress__bar"
        value={clamped === undefined ? undefined : clamped / 100}
        max={1}
        thickness={size === 'medium' ? 'large' : 'medium'}
        shape="rounded"
        color={tone === 'error' ? 'error' : tone === 'success' ? 'success' : 'brand'}
        aria-label={name}
        aria-valuetext={valueText ?? percentText}
      />
      {detail && <span className="ichtys-progress__detail">{detail}</span>}
    </div>
  );
};

export default GuideProgress;
